import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';
import * as uuid from 'uuid';

import { Address } from './address';
import { DateAdapter } from '../../client/helpers/serializer/adapters/date.adapter';
import { FatcaManagerCompany } from './fatca-manager-company';
import { FatcaManagerInterface } from '../../../core';
import { Id } from './id';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { Tax } from './tax';

@JsonObject('FatcaManager')
export class FatcaManager implements FatcaManagerInterface {
    private readonly _id: Id = undefined;

    @JsonProperty('Fullname', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('ResidenceData', Address, true)
    private _habitualResidence: Address = undefined;

    @JsonProperty('MailResidenceData', Address, true)
    private _mailResidence: Address = undefined;

    private _isEqualMailAddress: boolean = undefined;

    @JsonProperty('BirthDate', DateAdapter, true)
    private _birthDate: Moment = undefined;

    @JsonProperty('CityOfBirth', StringAdapter, true)
    private _cityOfBirth: string = undefined;

    @JsonProperty('CountryOfBirth', StringAdapter, true)
    private _countryOfBirth: string = undefined;

    @JsonProperty('ManagerCompanies', [FatcaManagerCompany], true)
    private _companies: FatcaManagerCompany[] = undefined;

    @JsonProperty('TaxResidence', Tax, true)
    private _tax: Tax = undefined;


    constructor() {
        this._id = Id.fromString(uuid.v4());
    }

    get id(): Id {
        return this._id;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get habitualResidence(): Address {
        if (!this._habitualResidence) {
            this._habitualResidence = new Address();
        }

        return this._habitualResidence;
    }

    get mailResidence(): Address {
        if (!this._mailResidence) {
            this._mailResidence = new Address();
        }

        return this._mailResidence;
    }

    set mailResidence(value: Address) {
        this._mailResidence = value;
    }

    get isEqualMailAddress(): boolean {
        if (this._isEqualMailAddress === undefined) {
            this._isEqualMailAddress = this.habitualResidence.equals(this.mailResidence);
        }

        return this._isEqualMailAddress;
    }

    set isEqualMailAddress(value: boolean) {
        this._isEqualMailAddress = value;
    }

    get birthDate(): Moment {
        return this._birthDate;
    }

    set birthDate(value: Moment) {
        this._birthDate = value;
    }

    get cityOfBirth(): string {
        return this._cityOfBirth;
    }

    set cityOfBirth(value: string) {
        this._cityOfBirth = value;
    }

    get countryOfBirth(): string {
        return this._countryOfBirth;
    }

    set countryOfBirth(value: string) {
        this._countryOfBirth = value;
        this._cityOfBirth = undefined;
    }

    get companies(): FatcaManagerCompany[] {
        if (!this._companies) {
            this._companies = [];
        }

        return this._companies;
    }

    addCompany(value: FatcaManagerCompany) {
        if (!this.companies.find(it => it.id.value === value.id.value)) {
            this.companies.push(value);
        }
    }

    updateCompany(value: FatcaManagerCompany) {
        this.companies.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.companies[idx] = value;
            }
        });
    }

    removeCompany(value: FatcaManagerCompany) {
        this.companies.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.companies.splice(idx, 1);
            }
        });
    }

    get tax(): Tax {
        if (!this._tax) {
            this._tax = new Tax();
        }

        return this._tax;
    }

    isComplete(): boolean {
        return !!this.name
            && !!this.countryOfBirth
            && !!this.cityOfBirth
            && !!this.birthDate
            && this.habitualResidence.isComplete()
            && this.mailResidence.isComplete()
            && this.tax.isComplete()
            && (this.companies.length > 0 ? !this.companies.some(company => !company.isComplete()) : true);
    }
}
